/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save, WandSparkles, ArrowBigRight, ArrowBigLeft } from "lucide-react";
import { useAdminCreateForum } from "@/services/forumService";
import { useAdminGetForumGroupsSWR } from "@/services/forumGroupService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import slugify from "slugify";


const initialState = {
    apiPayload: {
        name: '',
        description: '',
        slug: '',
        group_ids: [],
        skill_id: ""
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
    GENERATE_SLUG: 'generateSlug',
    ADD_GROUP: 'addGroup',
    REMOVE_GROUP: 'removeGroup',
    SET_SKILL: 'setSkill',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.GENERATE_SLUG:
            return { ...state, apiPayload: { ...state.apiPayload, slug: slugify(state.apiPayload.name.toLowerCase()) } }
        case ACTIONS.ADD_GROUP:
            return { ...state, apiPayload: { ...state.apiPayload, group_ids: [...state.apiPayload.group_ids, Number(action.payload)] } }
        case ACTIONS.REMOVE_GROUP:
            return { ...state, apiPayload: { ...state.apiPayload, group_ids: state.apiPayload.group_ids.filter((group_id) => group_id != action.payload) } }
        case ACTIONS.SET_SKILL:
            return { ...state, apiPayload: { ...state.apiPayload, skill_id: action?.payload?.id ?? "", name: action.payload ? action.payload.code + " - " + action.payload.name : "" } }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}

export default function Add() {

    const [ , setPageTitle,  , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()

    const {data: groups, } = useAdminGetForumGroupsSWR()
    const adminCreateForum = useAdminCreateForum()
    const {data: skills} = useAdminGetSkillsSWR()



    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Ajout d'un forum")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, 
            subtype: e.target.name, 
            payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value
        })     
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.name || !state.apiPayload.slug ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        let payload = state.apiPayload
        if (payload.skill_id == "") {
            payload.skill_id = null
        }

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            const result = await adminCreateForum(payload)

            navigate("/admin/forums/" + result.id + "/rights")
        } catch (error) {
            console.error('admin/competitions/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }





    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres du forum
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Ligne : Nom*/}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border rounded-md border-secondary ${state.apiPayload.skill_id != "" ? "bg-gray-100" : ""}`}
                            id="name" name="name" 
                            value={state.apiPayload.name}
                            onChange={handleChange}
                            required
                            disabled={state.apiPayload.skill_id != ""}
                            />
                    </div>
                </div>
                {/* Ligne : description */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="description">
                            Description
                        </label>
                        <input type="text" 
                            className={`w-full border rounded-md border-secondary}`}
                            id="description" name="description" 
                            value={state.apiPayload.description}
                            onChange={handleChange}
                            />
                    </div>
                </div>

                {/* Ligne : slug */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="slug">
                            Slug <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-row">
                            <input type="text" 
                                className={`w-full border rounded-md rounded-r-none border-secondary}`}
                                id="slug" name="slug" 
                                value={state.apiPayload.slug}
                                onChange={handleChange}
                                required
                            />
                            <button
                                onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.GENERATE_SLUG }) }}
                                className="flex gap-2 items-center border border-blue-500 rounded-md rounded-l-none border-l-0 px-2 py-1 text-base hover:bg-blue-200"
                            >
                                <WandSparkles size={24} className="text-primary" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Ligne : Métier associé au forum */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="skill_id">
                            Métier associé
                        </label>
                        <select
                            className={`w-full border rounded-md border-secondary`}
                            id="skill_id" name="skill_id" 
                            value={state.apiPayload.skill_id}
                            onChange={(e) => { dispatch({ type: ACTIONS.SET_SKILL, payload: skills?.filter((skill) => skill.id == e.target.value)[0] }) }}
                        >
                            <option value="">-</option>
                            {skills?.map((skill, i) => {
                                return (
                                    <option key={i} value={skill.id}>
                                        {skill.code} - {skill.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>

                {/* Liste des groupes associés au forum */}
                <span>Groupes associés au forum :</span>
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex-1 flex flex-col gap-2 bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                        <h3 className="text-secondary">Groupes disponibles</h3>
                        {groups?.filter((group) => !state.apiPayload.group_ids.includes(group.id)).map((group, i) => {
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-between hover:bg-slate-200 hover:rounded-md px-1 cursor-pointer hover:text-green-600"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_GROUP, payload: group.id })}}
                                >
                                    <span className="text-base">{group.name}</span>
                                    <ArrowBigRight size={24} className="" />
                                </div>
                            )
                        })}
                    </div>
                    <div className="flex flex-col gap-2 flex-1  bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                    <h3 className="text-secondary">Groupes associés</h3>
                        {groups && state?.apiPayload?.group_ids?.map((id, i) => {
                            const group = groups.find((group) => group.id === id)
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-left hover:bg-slate-200 hover:rounded-md hover:text-red-600 px-1 cursor-pointer"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_GROUP, payload: id })}}
                                >
                                    <ArrowBigLeft size={24} className="text-primary hover:text-blue-600" />
                                    <span className="text-base">{group.name}</span>
                                    
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="w-full gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Ajouter et gérer les droits
                    </button>
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }
                </div>
            </form>
        </>
    )
}